import { Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi.js';
import { Board as ScheduleBoard } from './ProductionSchedule.jsx';

// ── Shared UI atoms ───────────────────────────────────────────────────────────
function StatCard({ label, value, sub, accent, loading }) {
  return (
    <div className="stat-card" style={{ borderTopColor: accent || 'var(--pld-green)' }}>
      <div className="stat-value" style={{ color: accent ? accent : 'var(--text)' }}>
        {loading ? <span className="skeleton-text" style={{ width: 48, height: 28 }} /> : value ?? '—'}
      </div>
      <div className="stat-label">{label}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

function Card({ title, children, action }) {
  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title">{title}</span>
        {action}
      </div>
      <div className="card-body">{children}</div>
    </div>
  );
}

function RunStatusDot({ status }) {
  const colours = {
    planned:    '#9ba695',
    in_progress:'#2980b9',
    paused:     '#e67e22',
    completed:  '#27ae60',
    signed_off: '#22391f',
    cancelled:  '#c0392b',
  };
  return (
    <span style={{
      display: 'inline-block',
      width: 8, height: 8,
      borderRadius: '50%',
      background: colours[status] || '#ccc',
      marginRight: 6,
      flexShrink: 0,
    }} />
  );
}

// ── Active runs card ──────────────────────────────────────────────────────────
function ActiveRunsCard() {
  const { data: runs, loading, error } = useApi('/production/runs?status=in_progress&limit=8');

  return (
    <Card title="Active Production Runs">
      {loading && <p className="card-loading">Loading…</p>}
      {error && <p className="card-error">Could not load runs</p>}
      {!loading && !error && (runs?.runs ?? []).length === 0 && (
        <p className="card-empty">No runs in progress</p>
      )}
      {!loading && (runs?.runs ?? []).map(run => (
        <div key={run.id} className="run-row">
          <div className="run-row-left">
            <RunStatusDot status={run.status} />
            <div>
              <div className="run-ref">{run.run_ref}</div>
              <div className="run-meta">{run.recipe_name || run.business_context}</div>
            </div>
          </div>
          <div className="run-badge">
            <span className={`badge badge-${run.business_context === 'CONTRACT' ? 'green' : 'yellow'}`}>
              {run.business_context === 'CONTRACT' ? 'Contract' : 'Own brand'}
            </span>
          </div>
        </div>
      ))}
    </Card>
  );
}

// ── Alerts card ───────────────────────────────────────────────────────────────
function AlertsCard() {
  const { data, loading } = useApi('/notifications/unread?limit=6');

  return (
    <Card title="Active Alerts">
      {loading && <p className="card-loading">Loading…</p>}
      {!loading && (!data || data.length === 0) && (
        <p className="card-empty">No active alerts</p>
      )}
      {!loading && data?.map(n => (
        <div key={n.notification_id} className="alert-row">
          <div className={`alert-dot priority-dot-${n.priority}`} />
          <div className="alert-content">
            <div className="alert-title">{n.title}</div>
            <div className="alert-msg">{n.message?.slice(0, 80)}{n.message?.length > 80 ? '…' : ''}</div>
          </div>
        </div>
      ))}
    </Card>
  );
}

// ── SALSA compliance summary card ─────────────────────────────────────────────
function SalsaCard() {
  const { data, loading } = useApi('/salsa/dashboard');

  return (
    <Card title="SALSA Compliance">
      {loading && <p className="card-loading">Loading…</p>}
      {!loading && data && (
        <div className="salsa-grid">
          <div className={`salsa-item ${data.critical_non_conformances > 0 ? 'salsa-item--warn' : ''}`}>
            <div className="salsa-num">{data.open_non_conformances}</div>
            <div className="salsa-lbl">Open NCs</div>
          </div>
          <div className={`salsa-item ${data.open_complaints > 0 ? 'salsa-item--warn' : ''}`}>
            <div className="salsa-num">{data.open_complaints}</div>
            <div className="salsa-lbl">Complaints</div>
          </div>
          <div className={`salsa-item ${data.training_expiring_30_days > 0 ? 'salsa-item--caution' : ''}`}>
            <div className="salsa-num">{data.training_expiring_30_days}</div>
            <div className="salsa-lbl">Training exp. 30d</div>
          </div>
          <div className={`salsa-item ${data.overdue_maintenance > 0 ? 'salsa-item--caution' : ''}`}>
            <div className="salsa-num">{data.overdue_maintenance}</div>
            <div className="salsa-lbl">Maint. overdue</div>
          </div>
        </div>
      )}
    </Card>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────────────
// This week's production schedule, read-only. Hidden for anyone without
// Production Schedule access (the board request comes back 403).
function WeekScheduleCard() {
  const { data: board, loading, error } = useApi('/production-schedule/board');
  if (error) return null;
  return (
    <Card
      title="This week's production"
      action={<Link to="/production-schedule" style={{ fontSize: 12, color: 'var(--pld-green)' }}>Open schedule →</Link>}
    >
      {loading || !board
        ? <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: 0 }}>Loading…</p>
        : board.lanes.length === 0
          ? <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: 0 }}>No work areas set up yet — add them on the Production Schedule.</p>
          : <div style={{ overflowX: 'auto' }}><div style={{ minWidth: 720 }}><ScheduleBoard board={board} readOnly /></div></div>}
    </Card>
  );
}

export default function Dashboard() {
  const { data: dashStats, loading: statsLoading } = useApi('/production/stats');
  const { data: hmrcData, loading: hmrcLoading } = useApi('/hmrc/current-period');

  const stats = dashStats || {};

  return (
    <div className="dashboard">
      {/* Stat row */}
      <div className="stat-row">
        <StatCard
          label="Active runs"
          value={stats.active_runs}
          accent="var(--pld-green)"
          loading={statsLoading}
        />
        <StatCard
          label="Casks in bond"
          value={stats.casks_in_bond}
          loading={statsLoading}
        />
        <StatCard
          label="Stock alerts"
          value={stats.stock_alerts}
          accent={stats.stock_alerts > 0 ? 'var(--status-major)' : undefined}
          loading={statsLoading}
        />
        <StatCard
          label="HMRC W1 due"
          value={hmrcData?.days_until_due != null ? `${hmrcData.days_until_due}d` : '—'}
          sub={hmrcData ? `${hmrcData.period_label} · by ${hmrcData.due_label}` : undefined}
          accent={
            hmrcData?.days_until_due <= 7 ? 'var(--status-critical)' :
            hmrcData?.days_until_due <= 14 ? 'var(--status-major)' : undefined
          }
          loading={hmrcLoading}
        />
        <StatCard
          label="APPA return & duty due"
          value={hmrcData?.appa_days_until_due != null ? `${hmrcData.appa_days_until_due}d` : '—'}
          sub={hmrcData ? `${hmrcData.period_label} · by ${hmrcData.appa_due_label}` : undefined}
          accent={
            hmrcData?.appa_days_until_due <= 7 ? 'var(--status-critical)' :
            hmrcData?.appa_days_until_due <= 14 ? 'var(--status-major)' : undefined
          }
          loading={hmrcLoading}
        />
      </div>

      {/* Cards grid */}
      <WeekScheduleCard />

      <div className="dashboard-grid">
        <ActiveRunsCard />
        <AlertsCard />
        <SalsaCard />
      </div>

      <style>{`
        .dashboard { display: flex; flex-direction: column; gap: 24px; }

        /* Stat row */
        .stat-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
          gap: 16px;
        }
        @media (max-width: 900px) { .stat-row { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 480px) { .stat-row { grid-template-columns: 1fr; } }

        .stat-card {
          background: var(--surface);
          border-radius: var(--radius-lg);
          padding: 18px 20px 16px;
          border-top: 3px solid var(--pld-green);
          box-shadow: var(--shadow-sm);
        }

        .stat-value {
          font-size: 28px;
          font-weight: 700;
          line-height: 1;
          margin-bottom: 6px;
        }

        .stat-label {
          font-size: 12.5px;
          color: var(--text-muted);
          font-weight: 500;
        }

        .stat-sub {
          font-size: 11px;
          color: var(--text-light);
          margin-top: 2px;
        }

        /* Cards */
        .dashboard-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
        }
        @media (max-width: 1100px) { .dashboard-grid { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 680px)  { .dashboard-grid { grid-template-columns: 1fr; } }

        .card {
          background: var(--surface);
          border-radius: var(--radius-lg);
          box-shadow: var(--shadow-sm);
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .card-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 18px;
          border-bottom: 1px solid var(--border);
        }

        .card-title {
          font-size: 13px;
          font-weight: 600;
          color: var(--text);
        }

        .card-body { padding: 8px 0; }

        .card-loading,
        .card-error,
        .card-empty {
          padding: 16px 18px;
          color: var(--text-muted);
          font-size: 13px;
        }
        .card-error { color: var(--status-critical); }

        /* Run rows */
        .run-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 18px;
          border-bottom: 1px solid var(--border);
          gap: 12px;
        }
        .run-row:last-child { border-bottom: none; }
        .run-row-left { display: flex; align-items: center; gap: 8px; min-width: 0; }
        .run-ref { font-size: 13px; font-weight: 600; }
        .run-meta { font-size: 11px; color: var(--text-muted); margin-top: 1px; }

        /* Alert rows */
        .alert-row {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 10px 18px;
          border-bottom: 1px solid var(--border);
        }
        .alert-row:last-child { border-bottom: none; }
        .alert-dot {
          width: 8px; height: 8px;
          border-radius: 50%;
          flex-shrink: 0;
          margin-top: 4px;
        }
        .priority-dot-critical { background: var(--status-critical); }
        .priority-dot-high     { background: var(--status-major); }
        .priority-dot-medium   { background: var(--pld-green); }
        .priority-dot-low      { background: var(--text-light); }
        .alert-title { font-size: 13px; font-weight: 600; margin-bottom: 2px; }
        .alert-msg   { font-size: 12px; color: var(--text-muted); line-height: 1.4; }

        /* SALSA grid */
        .salsa-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1px;
          background: var(--border);
        }
        .salsa-item {
          background: var(--surface);
          padding: 14px 18px;
          text-align: center;
        }
        .salsa-item--warn   { background: #fef9f9; }
        .salsa-item--caution{ background: #fffdf4; }
        .salsa-num { font-size: 22px; font-weight: 700; }
        .salsa-lbl { font-size: 11px; color: var(--text-muted); margin-top: 3px; }

        /* Skeleton */
        .skeleton-text {
          display: inline-block;
          background: linear-gradient(90deg, var(--border) 25%, var(--surface-2) 50%, var(--border) 75%);
          background-size: 200%;
          animation: shimmer 1.4s infinite;
          border-radius: var(--radius-sm);
        }
        @keyframes shimmer { 0% { background-position: 200% } 100% { background-position: -200% } }
      `}</style>
    </div>
  );
}
